/**
 * TypeScript types for Drug Q&A feature
 */

export type VersionSourceType = 'fda_current' | 'fda_historical' | 'draft' | 'sba' | 'epar' | 'uk_pil' | 'ca_pm' | 'au' | 'eu' | 'jp_pdf' | 'zh_pdf' | 'ch_pdf' | 'de_pdf' | 'ie_pdf' | 'it_pdf' | 'fr_html' | 'fda_label_pdf' | 'approval_letter' | 'eu_historical' | 'eu_history_section';

export type MessageRole = 'user' | 'assistant';

/**
 * A message in the conversation history
 */
export interface ConversationMessage {
  role: MessageRole;
  content: string;
}

/**
 * Per-segment verdict from CEPI's structured-output verifier pass.
 *
 * Emitted on the ``verification`` SSE event (only when the backend's
 * ``CEPI_SEGMENT_VERIFIER_ENABLED`` flag is on), AFTER the buffered
 * ``token`` answer and BEFORE ``done``.  ``segment_index`` pairs to the
 * answer HTML's ``<div class="cepi-segment" data-segment="{i}">`` wrapper.
 * We FLAG, never strip — an ``unverified`` segment is still rendered; the
 * UI just marks it.  ``reason`` is a short human-readable explanation
 * (good tooltip text).
 */
export interface SegmentVerification {
  segment_index: number;
  status: 'verified' | 'unverified' | 'not_applicable';
  reason: string;
}

/**
 * A capability handoff CEPI proposes for a turn, from the `done` event.
 *
 * `scope_draft` is editable — the shell's capability card pre-fills from it and the user may
 * change it before execution. `member_drug_ids` travels with the draft so the
 * job cannot drift to a different cohort between the card being rendered and
 * the button being clicked.
 */
export type Capability = 'cogni' | 'keyword' | 'investigator';

export interface CapabilityScopeDraft {
  capability: Capability;
  criterion: string;
  /**
   * Populated ONLY for a pinned Investigator run — i.e. when the user is
   * looking at a result set. Empty means the capability discovers its own
   * scope, which is Investigator's native mode and the only mode Cogni and
   * Keyword have.
   */
  member_drug_ids: number[];
  member_count: number;
  /**
   * Two genuinely different guarantees, and they must never read the same:
   * `complete` = every one of these N products gets a verdict.
   * `discover` = the corpus is searched and whatever it finds gets a verdict,
   *              which says nothing about products it never surfaced.
   */
  coverage_mode: 'complete' | 'discover';
}

export interface CapabilityAction {
  type: 'capability_handoff';
  capability: Capability;
  /** User-facing, in terms of what the QUESTION needs — not what the tool is. */
  reason: string;
  /**
   * What running this does to the conversation's working set, or null when
   * nothing is attached.
   *
   * `narrow` — Investigator, the only capability whose endpoint takes a drug
   * list, so it evaluates exactly the set already attached.
   * `new_search` — Cogni and Keyword, which search the whole corpus because
   * `searchV2Drugs` has no drug-list parameter at all.
   *
   * SEMANTICS, not prose: the wording lives here in the frontend so `reason`
   * stays the model's own text. The predecessor of this field was a WARNING
   * ("Runs corpus-wide — does not inherit your attached results") which
   * described an ordinary operation as a confession, and so read as a bug.
   *
   * Always present. Optional-vs-null would make "nothing attached" and "an
   * older backend" indistinguishable.
   */
  operation: 'narrow' | 'new_search' | null;
  scope_draft: CapabilityScopeDraft;
  /**
   * Closed set, DERIVED server-side. `run` = CEPI drives; `open` = the user
   * drives. Only Investigator carries `run` today: its endpoint is the only
   * one that produces an addressable result CEPI can read back, so running
   * the others for the user would create work the conversation is blind to.
   */
  options: ('run' | 'open')[];
  /**
   * Point at CEPI Agents: true when a capability is being recommended while a
   * result is ALREADY attached, i.e. the user is visibly building a chain.
   *
   * A NUDGE, NEVER A GATE — the card still works at this hop and every hop
   * after it. Server-computed because the predecessor was a React state
   * counter (`attachHops`) that was session-scoped and reset on reload, so
   * the nudge silently depended on how long the tab had been open.
   *
   * Absent on an older backend -> false -> no nudge, which is the old
   * behaviour rather than a wrong claim.
   */
  agents_nudge: boolean;
}

/**
 * A message in the Q&A conversation (for UI state)
 */
export interface QAMessage {
  id: string;
  role: MessageRole;
  content: string;
  citations?: SimpleCitation[];
  // Per-segment verifier verdicts (CEPI structured-output path). Present
  // only when the backend's segment verifier is enabled and emitted a
  // ``verification`` event for this answer; otherwise undefined (no
  // verdicts => render the answer with no verification affordance).
  verification?: SegmentVerification[];
  /**
   * WHY this message is what it is, from the backend's ``done`` event.
   *
   *   ok                   a grounded answer
   *   evidence_incomplete  retrieval returned nothing usable, so no answer
   *                        was attempted — ``content`` is a placeholder
   *   no_answer            evidence existed but no grounded answer could be
   *                        written over it — ``content`` is a placeholder
   *
   * The last two arrive over a 200 with a human-readable body, so without
   * this field a non-answer renders identically to an answer. Absent on
   * messages from before the field existed (and on user messages); treat
   * undefined as ``ok``.
   */
  answer_status?: 'ok' | 'evidence_incomplete' | 'no_answer';
  /**
   * Capability handoff for this turn, from the `done` event's `actions`.
   * Present only when the ANSWERING MODEL judged another capability would
   * serve the request better — most turns carry nothing, and a card on a
   * question that did not need one is an affordance the user learns to
   * dismiss.
   *
   * The model supplies semantics only (which capability, why, the criterion);
   * the executable scope on `scope_draft` is assembled server-side from the
   * real request. So this is not a UI action the model authored — it is a
   * closed set the UI switches on.
   */
  capability_action?: CapabilityAction;
  timestamp: Date;
  isLoading?: boolean;
  error?: string;
  /** Restored from a stream that never finished (shell persistence). */
  interrupted?: boolean;
  /** Shell: this message's capability tab was auto-opened (D14). Persisted so a
   *  reload never opens it a second time. */
  opened?: boolean;
  /** Set on the assistant message whose user/assistant pair was pushed to the
   *  chat conversation history (shell trimming counts only these). */
  inHistory?: boolean;
  
  // NEW FIELDS for intent layer (same as DrugQAResponse)
  sections_used?: string[];
  token_usage?: {
    sections: number;
    diffs: number;
    comments: number;
    total: number;
  };
  
  // NEW FIELDS for over-budget handling
  over_budget?: boolean;
  warning?: string;
  token_breakdown?: {
    available_budget: number;
    total_requested: number;
    over_by: number;
    breakdown: {
      current_sections: {
        total: number;
        by_section: Record<string, number>;
      };
      version_diffs: Array<{
        version_id: number;
        version_number: number;
        source: string;
        tokens: number;
      }>;
      comments: number;
      overhead: number;
    };
  };
  suggestions?: string[];
}

// =============================================================================
// Simplified Q&A Types
// =============================================================================

/**
 * A section of the drug label
 */
export interface DrugSection {
  section_number?: string;
  title?: string;
  content_text?: string;
}

/**
 * A reviewer comment on a draft
 */
export interface DrugComment {
  author?: string;
  comment_text: string;
  context_text?: string;
}

/**
 * Context about a drug passed to the simple Q&A endpoint
 */
export interface DrugVersionContext {
  version_type: "current_fda" | "fda_historical" | "draft";
  version_info: string;
  sections: DrugSection[];
}

/**
 * Reference to a version that can be fetched on-demand
 */
export interface AvailableVersion {
  id: number;
  // Backend-prefixed version ID — 'current:<id>' for the live drug row,
  // 'historical:<id>' for historical versions.
  //
  // NOTE: This used to wire the CEPI version-chip selection upstream to
  // DrugQAContext.selected_version_ids. That selector + request field were
  // retired in the structured-output cutover (CEPI now reads any version by
  // ordinal at the tool layer). The type is retained only for the legacy
  // useQAStream hook; nothing populates/consumes typed_id in CEPI anymore.
  typed_id?: string;
  source: "fda_current" | "fda_historical" | "draft";
  version_number: number;
  brand_name?: string;
  effective_date?: string;
}

export interface DrugContext {
  brand_name: string;
  generic_name?: string;
  manufacturer?: string;
  sections?: DrugSection[];
  comments?: DrugComment[];
  version_info?: string;
  all_versions: DrugVersionContext[];
  current_version_type?: string;

  total_fda_versions?: number;
  total_draft_versions?: number;
  version_metadata?: {
    total_versions: number;
    latest_version: string;
    viewing_version: string;
  };

  version_intelligence?: {
    total_fda_versions: number;
    current_viewing: string;
    set_id: string;
    ndc_code: string;
  };

  // V2: Available versions that can be fetched on-demand for comparisons
  available_versions?: AvailableVersion[];

  // V2.1: User-selected versions for comparison (from QA version selector)
  selected_versions_for_comparison?: number[];

  // V2.2: Evidence sections from search pipeline (merged with intent layer picks)
  search_evidence_sections?: string[];
}

/**
 * A simple citation extracted from the LLM response
 */
export interface SimpleCitation {
  /** The concrete corpus on CEPI report citations, when no Q&A document type exists. */
  corpus?: string | null;
  // Optional: competitive_intel citations (see below) have no section — a
  // competitor page/press-release has none of section_number/version_type.
  // ``| undefined`` is explicit (not just ``?:``) because this project runs
  // ``exactOptionalPropertyTypes`` — several call sites assign a possibly-
  // undefined value through (e.g. ``version_type: citation.version_type``
  // where ``citation`` is itself a ``SimpleCitation``), which needs the
  // wider type to type-check under that flag.
  section_number?: string | undefined;
  section_title?: string;
  version_type?: VersionSourceType | undefined;
  // For SBA citation linking — scroll to exact location
  section_id?: number;
  chunk_id?: number;
  start_char?: number;
  end_char?: number;
  document_id?: number;
  anchor_start?: string;
  anchor_end?: string;
  anchor_length?: number;
  /**
   * The cited sentence(s) in full, joined — set by `dispatchCitation` from the
   * captured span's `texts`.
   *
   * Distinct from `anchor_start`/`anchor_end`, which are 60-character head and
   * tail probes sized for matching against a PDF text layer. Some consumers need
   * the whole sentence instead: the label handoff highlights for the LENGTH of
   * the text it is given, so a 60-char probe would highlight a fragment of the
   * evidence and imply the answer rested on that much.
   *
   * Set on the fall-through dispatch so it survives every route. Absent when no
   * span was captured, which is how a caller knows there is no claim to make.
   */
  evidence_text?: string;
  /**
   * One anchor per CITED SPAN, in order — the whole evidence, not just its
   * first piece.
   *
   * `anchor_start`/`anchor_end` above describe only the first span, which is
   * all the viewer used to receive. Measured on staging, 5 of 12 spanned
   * citations carry more than one span (evidence on two pages is ordinary when
   * a section runs across a page break), so the reader was shown a truthful but
   * incomplete highlight 42% of the time.
   *
   * Spans stay separate rather than merged into one range: everything adjacent
   * was already joined server-side (`_merge`, gap <= 1 char), so anything still
   * distinct is a genuinely different passage. One band stretched across them
   * would box the text in between, which the model never cited.
   */
  evidence_anchors?: {
    /** The OWNER chunk — what the highlight must anchor on. */
    chunk_id: number | null;
    /** The owner's page, for viewers that key by page rather than chunk. */
    page: number | null;
    start: string;
    end: string | null;
    /** The whole cited sentence. `start`/`end` are deliberately short — a long
     *  exact needle dies on one OCR error — but the matcher's fifth pass scores
     *  WHOLE sentences at character level, which is what separates two table
     *  rows differing only in a dose. Optional: absent on an older cached
     *  answer, and the fifth pass then simply does not run. */
    text?: string | null;
  }[];
  /**
   * Spans captured at answer time — the sentences the model said it used.
   *
   * Built server-side from the `[E<n>]` markers the model cited, so this is a
   * RECORD of what was used, not a reconstruction of it after the fact. That
   * is why it needs no network call and no LLM: by the time the citation
   * reaches the browser the span is already decided.
   *
   * `chunk_id` here is the chunk that OWNS the text, which is not always the
   * citation's own `chunk_id` — they differ when the cited sentence was
   * borrowed overlap, and the owner is the one whose page is correct.
   *
   * Absent whenever nothing resolved (an unmarked tool, a unit the marker
   * refused, an id the model invented). Absent means navigate, do not
   * highlight — the behaviour that shipped before any of this.
   */
  evidence_spans?: {
    // Null for a progressive-READ span, which is owned by a section rather
    // than a chunk (a read slice has no overlap assembly to attribute).
    chunk_id: number | null;
    section_id?: number | null;
    start: number;
    end: number;
    page?: number | null;
    unit_ids: string[];
    /**
     * OPTIONAL because this arrives untyped and can outlive the build that
     * wrote it. The first resolver (0e30a6519) emitted spans with no `texts`
     * at all; it landed one commit later, in 4f3aaae0a. Chat snapshots live in
     * sessionStorage and survive every deploy, so a tab open across that
     * boundary hands today's code yesterday's shape — which crashed the whole
     * panel behind the error boundary on a plain refresh.
     *
     * Declaring it optional is the fix rather than a guard at the crash site:
     * it makes the compiler point at every consumer, instead of leaving the
     * next one to find out in production.
     */
    texts?: string[];
  }[];
  // ORIGIN lane (version_type === 'fda_historical' on a `label` citation):
  // the `drug_versions.id` the viewer opens via ('fda_historical', id). Set by
  // the backend only for origin-version evidence; absent on current-label
  // citations, which keep version_type 'fda_current'.
  drug_version_id?: number;
  // (the origin version's number rides on the existing ``version_number``
  // field declared further down — not redeclared here)
  // false = this is the OLDEST VERSION WE HOLD, not provably the original
  // label (DailyMed history was never fully harvested for ~23% of products).
  is_true_origin?: boolean;
  // NEW (Phase D Task 9): document-type discriminator. Matches the backend
  // ``UnifiedCitation.document_type``.  ``MessageBubble.handleContentClick``
  // dispatches letter citation clicks off this field; the existing
  // label/sba/epar consumers leave it ``undefined`` and behave as before.
  document_type?: 'label' | 'sba' | 'epar' | 'uk_pil' | 'ca_pm' | 'au' | 'eu' | 'jp_pdf' | 'zh_pdf' | 'ch_pdf' | 'de_pdf' | 'ie_pdf' | 'it_pdf' | 'fr_html' | 'fda_label_pdf' | 'approval_letter' | 'fda_letter' | 'eu_historical' | 'eu_history_section' | 'competitive_intel';
  // eu_historical only: the archived version pair the citation opens the EU
  // historical COMPARE at (drug_id + section_number carry the rest). The click
  // deep-links the compare to (from_version → to_version) at ``section_number``.
  from_version?: string | null;
  to_version?: string | null;
  // eu_historical: content-addressed id of the EXACT evidence block this
  // citation grounds to (from ``eu_version_diff_blocks``). When present, the
  // compare deep-link resolves it via /eu-history/.../compare-block and scrolls
  // + outlines the precise old/new spans of that change — not just the section.
  // Absent → the compare falls back to landing on ``section_number``.
  // competitive_intel: reused as the CI evidence block identity (the exact
  // page/press-release/diff-block id ``get_competitor_page`` /
  // ``get_competitor_page_diff`` cited) — see the CI-specific fields below.
  block_id?: string | null;
  // eu_historical / eu_history_section (flag-ON Parser-B path): which EU document
  // PART the citation grounds to — 'smpc' | 'annex_ii' | 'labelling' |
  // 'package_leaflet'. Matches the backend ``UnifiedCitation.document_part``.
  // Part-namespaces the block_id (a SmPC §4 id must never resolve a leaflet §4
  // block) and is threaded into the compare deep-link. Absent on the legacy
  // SmPC-only path (treated as 'smpc' downstream).
  document_part?: string | null;
  // eu_history_section only: a COMPLETE-section read of ONE archived version.
  // The click opens the INDIVIDUAL historical PDF viewer (document_id) scrolled
  // to ``page`` with ``highlight_text`` boxed. (drug_id + section_number carry
  // the rest.) Distinct from eu_historical, which opens the compare.
  version?: string | null;
  // eu_history_section: the STABLE version key (``source_version_key`` == vstr)
  // that survives a re-ingest. Preferred over the volatile ``document_id`` when
  // deep-linking the archived viewer (?euver=), so a persisted citation never
  // goes stale. Absent on legacy citations → fall back to ``document_id``.
  source_version_key?: string | null;
  page?: number | null;
  // Normalized Y (0..1) of the cited section's top on ``page`` — scroll fallback
  // when the section isn't resolvable via the TOC (US-SBA parity otherwise).
  highlight_y?: number | null;
  // eu_history_section: the auto-selected strength — the archived viewer passes
  // it to the section-geometry endpoint so a multi-strength version boxes the
  // right presentation's section.
  presentation_key?: string | null;
  highlight_text?: string | null;
  /** Chunk-local evidence is navigable but must not request a page highlight. */
  citation_alignment?: 'source_aligned' | 'chunk_only' | null;
  citation_alignment_reason?: string | null;
  coordinate_space?: string | null;
  text_basis?: string | null;
  source_checksum?: string | null;
  // NEW (Phase D Task 9): letter linkage for the hybrid routing rule.
  //   linked   → ``/drug/:drug_id?source=fda-letter&version=:letter_id``
  //   unlinked → ``/fda-letter/:letter_id``
  //
  // Nullable because the backend ``UnifiedCitation.letter_id``/``drug_id``
  // are ``Optional[int] = None`` and Pydantic v2 serializes unset
  // optional fields as ``null`` on the wire (no ``exclude_none``).  So a
  // drug citation will arrive with ``letter_id: null`` (and vice versa).
  // Matches the nullability convention used by
  // ``KeywordSearchResultItem.drug_id?: number | null``.
  letter_id?: number | null;
  drug_id?: number | null;
  // Owning drug's display name (brand, else generic), resolved by the
  // backend so the Sources chip can show e.g. "Keytruda" instead of
  // "Drug #138559". Absent when the name couldn't be resolved.
  drug_name?: string | null;
  // FDA-printed / bundle-local presentation number (SBA bundle-split
  // consumers, Task 8). ADDITIVE display metadata ONLY — ``section_number``
  // above remains the click-target / navigation key everywhere (MessageBubble
  // dispatch, Drug Detail navigation, etc. all keep reading ``section_number``
  // unchanged). Consumers that want to SHOW the number the way FDA printed it
  // (rather than the internal ``SEG<N>[.x.y]`` token) read this field and fall
  // back to ``section_number`` when absent — every non-bundle-split citation.
  section_number_local?: string | null;
  // Logical HA source identity for display and client-side diagnostics. The
  // physical document_id above remains the citation/viewer click target.
  document_title?: string | null;
  version_labels?: string[];
  logical_document_key?: string | null;
  // competitive_intel-specific: mirrors the backend ``UnifiedCitation``
  // competitive_intel fields 1:1 (`unified_chat_schemas.py`). This is the
  // FIRST citation type with an EXTERNAL href — ``external_url`` (a
  // competitor's own website / press release), never an in-app viewer
  // route. Always gate through ``safeCompetitiveIntelUrl`` (HTTPS-only)
  // before rendering it as a real link — see utils/competitiveIntelUrl.ts.
  // ``document_title`` (above) carries the page/release title;
  // ``company_name`` is the tracked competitor. A page citation carries
  // page_id/page_version_id/version_number; a diff-block citation carries
  // page_id + from_version_number/to_version_number instead (mutually
  // exclusive per the backend's result shapes); a press-release citation
  // carries press_release_id only.
  external_url?: string | null;
  company_name?: string | null;
  page_id?: number | null;
  page_version_id?: number | null;
  press_release_id?: number | null;
  version_number?: number | null;
  from_version_number?: number | null;
  to_version_number?: number | null;
}

export const DEFAULT_ANCHOR_LENGTH = 1500;

export interface ChunkClickData {
  /** NULL for a label progressive read, which owns no chunk. Nothing on the
   *  receiving side reads it — `doChunkHighlight` locates the section by
   *  `sectionNumber` and the text by the anchors — so the highlight is
   *  unaffected; the field stays for the chunk-addressed callers that do set
   *  it. */
  chunkId: number | null;
  sectionNumber: string;
  anchorStart: string;
  anchorEnd: string;
  anchorLength: number;
}
